/* eslint-disable no-param-reassign */
import { WritableDraft } from 'immer';
import { core_v1_DataSource_FieldSpec } from '@antimatterhq/asl-api-ts-v1';
import {
  DatasourceUpgradeDiff,
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeItemDiff,
  DatasourceUpgradeTableDiff,
  DatasourceUpgradeMedallionDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeFieldSpecDiff,
} from '../datasourceUpgrade.types';
import { setFiltersText } from '../fieldUtils/filters/datasourceUpgradeFiltersHelper';
import {
  acceptField,
  acceptTemporaryField,
  rejectField,
  rejectTemporaryField,
  setFieldSpecText,
} from '../fieldSpec/datasourceUpgradeFieldSpecHelper';
import { setPreserveUnreferencedFieldsText } from '../fieldUtils/preserveUnreferencedFields/datasourceUpgradePreserveUnreferencedFieldsHelper';
import { filterNulls } from '@/utils/filterNulls';
import {
  Datasource,
  Datasource_Preset,
  Datasource_Preset_Silver_Transform,
  Datasource_Silver_AddTable,
  Datasource_Silver_ModifyTable,
} from '@/types/api';

export const getSilverChanges = (
  upgradeState: DatasourceUpgradeDiff | undefined,
): DatasourceUpgradeTableDiff[] => {
  if (!upgradeState?.spec.silver.transform) {
    return [];
  }

  return upgradeState.spec.silver.transform.map((transform) => {
    const { diff } = transform;

    // we all only want the table changes if the table has not been added or removed
    if (diff.status === 'unchanged') {
      diff.changes = filterNulls([
        ...(transform.fields || []),
        transform.filter,
        transform.postFilter,
        transform.utils?.unreferencedColumns?.preserve,
        transform.utils?.unreferencedColumns?.embedColumn,
        transform.utils?.unreferencedColumns?.embedColumnType,
        transform.utils?.unreferencedColumns?.omitColumns,
        transform.utils?.unreferencedColumns?.duplicatePrefix,
        ...(transform.utils?.temporaryFields || []),
      ]).filter((change) => change.status !== 'unchanged');

      // else if the table has been added or removed, we want to show the diff
    } else if (transform.diff.status !== 'unchanged') {
      diff.changes = [transform.diff];
    }

    return diff;
  });
};

export const setSilverTableText = (tableDiff: DatasourceUpgradeTableDiff) => {
  if (tableDiff.status === 'unchanged') {
    return;
  }

  if (tableDiff.currentPresetValue) {
    tableDiff.title = 'table removed';
    tableDiff.description = `"${tableDiff.tableName}" table removed`;
    tableDiff.content = tableDiff.currentPresetValue?.fields
      ?.map((field) => {
        return field?.name;
      })
      .join(', ');
  }

  if (tableDiff.upgradePresetValue) {
    tableDiff.title = 'table added';
    tableDiff.description = `"${tableDiff.tableName}" table added`;
    tableDiff.content = tableDiff.upgradePresetValue?.fields
      ?.map((field) => {
        return field?.name;
      })
      .join(', ');
  }
};

export const setSilverText = (
  upgradeDiff: DatasourceUpgradeDiff | undefined,
) => {
  if (!upgradeDiff?.spec.silver.transform) {
    return;
  }

  upgradeDiff.spec.silver.transform.forEach((transform) => {
    transform.fields?.forEach((field) => {
      setFieldSpecText(field);
    });
    setFiltersText(transform);
    setPreserveUnreferencedFieldsText(transform.utils);
    transform.utils?.temporaryFields?.forEach((field) => {
      setFieldSpecText(field);
    });
    setSilverTableText(transform.diff);
  });
};

export const querySilverChanges = (
  silver: DatasourceUpgradeMedallionDiff,
  query: { status?: DatasourceUpgradeFieldStatus } = {},
) => {
  return silver.tables.reduce<Array<DatasourceUpgradeItemDiff>>(
    (acc, table) => {
      return [
        ...acc,
        ...table.changes.filter(
          (change) => !query?.status || change.status === query.status,
        ),
      ];
    },
    [],
  );
};

export const getSilverTable = (datasource: Datasource, tableName: string) => {
  const table =
    datasource.spec.silver?.transform?.presetOverrides?.modifyTables?.find(
      (t) => t.name === tableName,
    );

  if (!table) {
    throw new Error(`Unable to find silver table "${tableName}"`);
  }

  return table;
};

export const setSilverFilterChange = ({
  tableName,
  datasource,
  value,
}: {
  tableName: string;
  datasource: WritableDraft<Datasource>;
  value: string | undefined;
}) => {
  const table = getSilverTable(datasource, tableName);

  table.filter = value;
};

export const setSilverPostFilterChange = ({
  tableName,
  datasource,
  value,
}: {
  tableName: string;
  datasource: WritableDraft<Datasource>;
  value: string | undefined;
}) => {
  const table = getSilverTable(datasource, tableName);

  table.postFilter = value;
};

export const getSilverTableStatus = ({
  datasource,
  upgradePresetValue,
  tableName,
}: {
  datasource: Datasource;
  upgradePresetValue: Datasource_Preset;
  tableName: string;
}): DatasourceUpgradeFieldStatus => {
  const isCurrentlyOmitted =
    datasource.spec.silver?.transform?.presetOverrides?.omitTables?.includes(
      tableName,
    );

  // if the table is omitted we don't care if it's been added or removed
  if (isCurrentlyOmitted) {
    return 'unchanged';
  }

  const existsInUpgradePreset = upgradePresetValue.silver?.transform?.some(
    (t) => t.name === tableName,
  );
  const existsInDatasource =
    datasource.spec.silver?.transform?.presetOverrides?.modifyTables?.some(
      (t) => t.name === tableName,
    );

  // table added
  if (!existsInDatasource && existsInUpgradePreset) {
    return 'unapplied';
  }

  // table removed
  if (existsInDatasource && !existsInUpgradePreset) {
    return 'unapplied';
  }

  return 'unchanged';
};

export const setSilverTableStatus = ({
  draft,
  status,
  tableName,
}: {
  draft: WritableDraft<DatasourceUpgradeStatus>;
  status: DatasourceUpgradeFieldStatus;
  tableName: string;
}) => {
  const table = draft.spec.silver?.transform?.find((t) => t.name === tableName);

  if (!table) {
    throw new Error(`Unable to find silver table "${tableName}"`);
  }

  table.status = status;
};

export const acceptSilverTableChanges = ({
  diff,
  datasource,
}: {
  diff: DatasourceUpgradeTableDiff;
  datasource: WritableDraft<Datasource>;
}) => {
  if (diff.status === 'unchanged') {
    throw new Error(
      `Cannot accept unchanged silver table changes for ${diff.tableName}`,
    );
  }

  const { currentPresetValue, upgradePresetValue } = diff;

  const isTableAdded = !currentPresetValue && upgradePresetValue;
  // if we are adding a table we don't need to do anything
  if (isTableAdded) {
    return;
  }

  if (!datasource.spec.silver?.transform?.presetOverrides?.modifyTables) {
    throw new Error(
      `Cannot accept silver table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  // we are deleting the table so remove any references to modifyTables
  const { modifyTables } = datasource.spec.silver.transform.presetOverrides;

  datasource.spec.silver.transform.presetOverrides.modifyTables =
    modifyTables.filter((t) => t.name !== diff.tableName);
};

const rejectSilverTableChanges = ({
  diff,
  datasource,
}: {
  diff: DatasourceUpgradeTableDiff;
  datasource: WritableDraft<Datasource>;
}) => {
  if (diff.status === 'unchanged') {
    throw new Error(
      `Cannot reject unchanged silver table changes for ${diff.tableName}`,
    );
  }

  if (diff.medallion !== 'silver') {
    throw new Error(
      `Cannot reject silver table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  const { currentPresetValue, upgradePresetValue } = diff;

  const isTableAdded = !currentPresetValue && upgradePresetValue;

  if (!datasource.spec.silver?.transform?.presetOverrides) {
    throw new Error(
      `Cannot reject silver table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  // if we are rejecting and added table then we need to omit it
  if (isTableAdded) {
    datasource.spec.silver.transform.presetOverrides.omitTables = [
      ...(datasource.spec.silver.transform.presetOverrides.omitTables || []),
      diff.tableName,
    ];
    return;
  }

  // else we need to find the modified table and add to add tables

  if (!datasource.spec.silver.transform.presetOverrides.addTables) {
    datasource.spec.silver.transform.presetOverrides.addTables = [];
  }

  const addTables = datasource.spec.silver.transform.presetOverrides.addTables;

  if (addTables.some((t) => t.name === diff.tableName)) {
    console.warn(
      `Cannot reject silver table changes for ${diff.tableName} because the table is in the addTables preset`,
    );
    return;
  }

  const modifyTable = diff.currentValue;

  if (!modifyTable) {
    throw new Error(
      `Unable to find silver table "${diff.tableName}" for rejecting table removal`,
    );
  }

  // we are rejecting the table removal so we need to add it back to the addTables
  addTables.push({
    name: diff.tableName,
    fields: currentPresetValue?.fields || [],
    filter: modifyTable.filter,
    postFilter: modifyTable.postFilter,
    utils: modifyTable.utils,
    custom: modifyTable.custom,
    overrideLiquidColumns: modifyTable.overrideLiquidColumns,
  });
};

export const getSilverTableDiff = ({
  currentValue,
  upgradeValue,
  currentPresetValue,
  upgradePresetValue,
  status,
  upgradeOmitTables,
  upgradedAddTable,
}: {
  currentValue: Datasource_Silver_ModifyTable | undefined;
  upgradeValue: Datasource_Silver_ModifyTable | undefined;
  currentPresetValue: Datasource_Preset_Silver_Transform | undefined;
  upgradePresetValue: Datasource_Preset_Silver_Transform | undefined;
  status: DatasourceUpgradeFieldStatus;
  upgradeOmitTables: string[];
  upgradedAddTable: Datasource_Silver_AddTable | undefined;
}): DatasourceUpgradeTableDiff => {
  const tableName = upgradePresetValue?.name || currentValue?.name || '';
  return {
    tableName,
    type: 'table',
    medallion: 'silver',
    changes: [], // changes get added in getSilverChanges
    status,
    currentValue,
    upgradeValue,
    currentPresetValue,
    upgradePresetValue,
    upgradedAddTable,
    upgradeOmitted: upgradeOmitTables.includes(tableName),
    updateStatus: (params: {
      draft: WritableDraft<DatasourceUpgradeStatus>;
      status: DatasourceUpgradeFieldStatus;
    }) =>
      setSilverTableStatus({
        ...params,
        tableName,
      }),
    acceptChanges: (params: {
      diff: DatasourceUpgradeTableDiff;
      datasource: WritableDraft<Datasource>;
    }) =>
      acceptSilverTableChanges({
        ...params,
      }),
    rejectChanges: (params: {
      diff: DatasourceUpgradeTableDiff;
      datasource: WritableDraft<Datasource>;
    }) =>
      rejectSilverTableChanges({
        ...params,
      }),
  };
};

export const rejectTemporarySilverField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getSilverTable(datasource, tableName!);

  if (!table.utils?.temporaryFields) {
    table.utils = {
      ...table.utils,
      temporaryFields: [],
    };
  }

  rejectTemporaryField(fieldDiff, table.utils);
};

export const acceptTemporarySilverField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getSilverTable(datasource, tableName!);

  if (!table.utils?.temporaryFields) {
    table.utils = {
      ...table.utils,
      temporaryFields: [],
    };
  }

  acceptTemporaryField(fieldDiff, table.utils);
};

export const rejectSilverField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getSilverTable(datasource, tableName!);

  rejectField(
    (fields: string[]) => (table.omitFields = fields),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};

export const acceptSilverField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getSilverTable(datasource, tableName!);

  acceptField(
    (fields: string[]) => (table.omitFields = fields),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};
